import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Boxes, UtensilsCrossed, ClipboardCheck, Plus, Pencil, Trash2, X, ChevronRight, ChevronLeft, CheckCircle2, XCircle, Loader2, Flame, PlayCircle } from "lucide-react";
import api from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";
import { Logo } from "../../components/Logo";
import { Card, Button, Field, Input, Spinner, PageGuide } from "../../components/ui-kit";

// Stessa lista di backend/allergens.py (ALLERGENS) — il form pubblico non ha
// sessione autenticata, quindi non può chiamare /app/catalog/allergens.
const ALLERGENS = [
  { key: "glutine", label: "Glutine" },
  { key: "crostacei", label: "Crostacei" },
  { key: "uova", label: "Uova" },
  { key: "pesce", label: "Pesce" },
  { key: "arachidi", label: "Arachidi" },
  { key: "soia", label: "Soia" },
  { key: "latte", label: "Latte (incluso lattosio)" },
  { key: "frutta_guscio", label: "Frutta a guscio" },
  { key: "sedano", label: "Sedano" },
  { key: "senape", label: "Senape" },
  { key: "sesamo", label: "Semi di sesamo" },
  { key: "solfiti", label: "Anidride solforosa e solfiti" },
  { key: "lupini", label: "Lupini" },
  { key: "molluschi", label: "Molluschi" },
];
const allergenLabel = (key) => ALLERGENS.find((a) => a.key === key)?.label || key;

const STEPS = [
  { key: "fornitori", label: "Fornitori", Icon: Truck },
  { key: "ingredienti", label: "Ingredienti", Icon: Boxes },
  { key: "ricette", label: "Ricette", Icon: UtensilsCrossed },
  { key: "riepilogo", label: "Riepilogo e invio", Icon: ClipboardCheck },
];

const STEP_GUIDES = {
  fornitori: {
    summary: "Iniziamo dai tuoi fornitori: chi ti porta la farina, la mozzarella, la carne...",
    steps: [
      "Scrivi il nome del fornitore (obbligatorio) e, se vuoi, partita IVA/email/telefono.",
      "Premi \"Aggiungi fornitore\": lo vedi comparire nella tabella sopra, e si salva da solo.",
      "Ripeti per ogni fornitore. Sbagliato qualcosa? Tocca la matita per modificarlo, o il cestino per toglierlo.",
      "Quando hai finito, premi \"Avanti\" per passare agli ingredienti.",
    ],
  },
  ingredienti: {
    summary: "Ora le materie prime che usi nelle ricette. Gli allergeni vengono suggeriti da soli dal nome.",
    steps: [
      "Scrivi il nome dell'ingrediente (obbligatorio): appena esci dal campo, il sistema propone da solo gli allergeni più probabili.",
      "Controlla il suggerimento e correggilo se serve: tocca un allergene per aggiungerlo o toglierlo.",
      "Se hai già inserito i fornitori, puoi collegare chi te lo fornisce (facoltativo).",
      "Premi \"Aggiungi ingrediente\". Con \"Indietro\" torni ai fornitori, con \"Avanti\" passi alle ricette.",
    ],
  },
  ricette: {
    summary: "Infine le tue ricette: nome, quali ingredienti usi e se vanno in cottura. Se hai già l'app con fornitori e materie prime caricati, puoi venire dritto qui: trovi già tutto pronto da scegliere.",
    steps: [
      "Scrivi il nome della ricetta e seleziona gli ingredienti: quelli già presenti in azienda sono già pronti da scegliere, oppure usa quelli aggiunti in questa sessione.",
      "Ti manca una materia prima per questa ricetta? Tocca \"+ Aggiungi materia prima\" qui sotto: la crei e la selezioni sul momento, senza perdere quello che hai già scritto.",
      "Indica se la ricetta prevede una cottura.",
      "Premi \"Aggiungi ricetta\": il resto (procedura, conservazione...) lo completerà il tuo consulente.",
      "Quando hai aggiunto tutte le ricette, premi \"Avanti\" per il riepilogo finale.",
    ],
  },
  riepilogo: {
    summary: "Ultimo passaggio: controlla tutto quello che hai inserito prima di mandarlo al tuo consulente.",
    steps: [
      "Scorri le tre tabelle: se manca qualcosa, torna indietro con lo step corrispondente a sinistra.",
      "Quando è tutto corretto, premi \"Ho finito, invia al mio consulente\".",
      "Non preoccuparti se ti accorgi di un errore dopo l'invio: potrai sempre tornare ad aggiungere altro.",
    ],
  },
};

const tempId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `t${Date.now()}${Math.random()}`);
// Stessa normalizzazione di backend/routers_catalog.py (_normalized_ingredient_name):
// case/accento-insensibile, per riconoscere "Acqua" e "acqua" come lo stesso nome.
const normalizedName = (name) => (name || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim().replace(/\s+/g, " ");
const errCls = "border-red-500 ring-1 ring-red-300 bg-red-50/40";
const thCls = "px-3 py-2";
const tdCls = "px-3 py-2 align-top";

function toggleIn(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function Table({ columns, empty, children }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-hairline">
      <table className="w-full min-w-[520px] text-sm">
        <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
          <tr>{columns.map((c) => <th key={c} className={thCls}>{c}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-hairline">
          {React.Children.count(children) > 0 ? children : (
            <tr><td colSpan={columns.length} className="px-3 py-6 text-center text-slate-400">{empty}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function RowActions({ onEdit, onRemove, label }) {
  return (
    <div className="flex gap-1">
      <button type="button" onClick={onEdit} className="grid h-9 w-9 place-items-center rounded-lg bg-gold text-white hover:bg-gold-light" aria-label={`Modifica ${label}`}><Pencil className="w-4 h-4" /></button>
      <button type="button" onClick={onRemove} className="grid h-9 w-9 place-items-center rounded-lg text-red-500 hover:bg-red-50" aria-label={`Rimuovi ${label}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

// Tour guidato interattivo: evidenzia i veri campi del modulo, passo dopo
// passo, cambiando automaticamente sezione quando serve.
const TOUR_STEPS = [
  { wizardStep: 0, selector: '[data-testid="intake-stepper"]', title: "Il percorso in 4 passi", text: "Qui vedi sempre a che punto sei: 4 passaggi numerati. Puoi tornare a uno precedente in qualsiasi momento toccandolo." },
  { wizardStep: 0, selector: '[data-testid="intake-supplier-name"]', title: "Passo 1 — Fornitori", text: "Scrivi qui il nome del fornitore: ad esempio chi ti porta la farina o la mozzarella." },
  { wizardStep: 0, selector: '[data-testid="intake-add-supplier"]', title: "Salva il fornitore", text: "Premi qui per aggiungerlo alla lista. Ripeti per ogni fornitore che usi, poi vai avanti." },
  { wizardStep: 1, selector: '[data-testid="intake-ingredient-name"]', title: "Passo 2 — Ingredienti", text: "Scrivi il nome dell'ingrediente. Appena esci dal campo, gli allergeni vengono proposti da soli." },
  { wizardStep: 1, selector: '[data-testid="intake-ingredient-suppliers"]', title: "Non dimenticare il fornitore", text: "Questo passaggio si salta facilmente per sbaglio: tocca qui il fornitore da cui acquisti questo ingrediente, se lo conosci." },
  { wizardStep: 1, selector: '[data-testid="intake-add-ingredient"]', title: "Salva l'ingrediente", text: "Premi qui per aggiungerlo alla lista, poi passa alle ricette." },
  { wizardStep: 2, selector: '[data-testid="intake-recipe-name"]', title: "Passo 3 — Ricette", text: "Scrivi il nome della ricetta, scegli gli ingredienti usati e indica se va in cottura." },
  { wizardStep: 3, selector: '[data-testid="intake-finish"]', title: "Passo 4 — Invio", text: "Controlli tutto un'ultima volta e premi qui per inviare al tuo consulente. Fatto!" },
];

function useTourTargetRect(selector, tick) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!selector) { setRect(null); return undefined; }
    const el = document.querySelector(selector);
    if (!el) { setRect(null); return undefined; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const read = () => setRect(el.getBoundingClientRect());
    const raf = requestAnimationFrame(read);
    window.addEventListener("resize", read);
    window.addEventListener("scroll", read, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", read);
      window.removeEventListener("scroll", read, true);
    };
  }, [selector, tick]);
  return rect;
}

function GuidedTour({ index, onNext, onPrev, onClose, currentWizardStep }) {
  const total = TOUR_STEPS.length;
  const tourStep = TOUR_STEPS[index];
  const onRightStep = tourStep.wizardStep === currentWizardStep;
  const rect = useTourTargetRect(onRightStep ? tourStep.selector : null, `${index}:${currentWizardStep}`);
  const onNextRef = React.useRef(onNext);
  onNextRef.current = onNext;

  // Alcuni riquadri (es. "collega il fornitore") esistono solo se il cliente
  // ha già inserito dati: se il campo non compare entro breve tempo, il tour
  // salta al passo successivo invece di restare bloccato ad aspettare.
  useEffect(() => {
    if (!onRightStep || rect) return undefined;
    const timer = setTimeout(() => onNextRef.current(), 900);
    return () => clearTimeout(timer);
  }, [onRightStep, rect]);

  if (!rect || !onRightStep) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
        <Loader2 className="h-6 w-6 animate-spin text-white" />
      </div>
    );
  }

  const pad = 6;
  const top = rect.top - pad, left = rect.left - pad, width = rect.width + pad * 2, height = rect.height + pad * 2;
  const vw = window.innerWidth, vh = window.innerHeight;
  const showBelow = top + height + 190 < vh;
  const cardWidth = Math.min(340, vw - 32);
  const cardLeft = Math.min(Math.max(left, 16), vw - 16 - cardWidth);

  return (
    <div className="fixed inset-0 z-[200]" role="dialog" aria-modal="true" aria-label="Tour guidato">
      <div className="fixed bg-black/60" style={{ top: 0, left: 0, right: 0, height: Math.max(top, 0) }} />
      <div className="fixed bg-black/60" style={{ top: top + height, left: 0, right: 0, bottom: 0 }} />
      <div className="fixed bg-black/60" style={{ top, left: 0, width: Math.max(left, 0), height }} />
      <div className="fixed bg-black/60" style={{ top, left: left + width, right: 0, height }} />
      <div className="fixed rounded-lg ring-2 ring-gold pointer-events-none" style={{ top, left, width, height }} />
      <div
        className="fixed rounded-xl bg-white p-4 shadow-2xl"
        style={{ width: cardWidth, left: cardLeft, top: showBelow ? top + height + 12 : undefined, bottom: showBelow ? undefined : Math.max(vh - top + 12, 16) }}
      >
        <div className="text-xs font-semibold uppercase tracking-wider text-gold">Passo {index + 1} di {total}</div>
        <h4 className="mt-1 font-heading font-semibold text-navy">{tourStep.title}</h4>
        <p className="mt-1 text-sm text-slate-600">{tourStep.text}</p>
        <div className="mt-3 flex items-center justify-between gap-2">
          <button type="button" onClick={onClose} className="text-xs text-slate-400 underline">Salta il tour</button>
          <div className="flex gap-2">
            {index > 0 && <Button variant="outline" onClick={onPrev}><ChevronLeft className="w-4 h-4" /></Button>}
            <Button variant="success" onClick={onNext}>{index === total - 1 ? "Fine" : "Avanti"} {index < total - 1 && <ChevronRight className="w-4 h-4" />}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function IntakeForm() {
  const { token } = useParams();
  const [state, setState] = useState(undefined); // undefined=carico, null=non valido
  const [tenantName, setTenantName] = useState("");
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState(false);
  const [tour, setTour] = useState(null); // null = tour non attivo, altrimenti indice del passo
  const [closed, setClosed] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  // Domanda iniziale che instrada: chi ha già l'app con fornitori/materie
  // prime caricati va dritto a Ricette, chi parte da zero segue i 3 passi.
  // null finché non risponde; resta "risposto" per tutta la visita anche
  // se poi si muove liberamente tra gli step con lo stepper.
  const [entryChoice, setEntryChoice] = useState(null);

  useEffect(() => {
    if (tour === null) return;
    const target = TOUR_STEPS[tour];
    if (target.wizardStep !== step) setStep(target.wizardStep);
  }, [tour]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cambiando step il contenuto sopra si sostituisce, ma se il cliente ha
  // scrollato fino al pulsante "Avanti"/"Aggiungi nuovi..." in fondo alla
  // pagina (frequente da telefono) il nuovo step resta fuori dallo schermo:
  // sembra che il tocco non abbia fatto nulla. Il tour gestisce già il suo
  // scroll mirato sul campo giusto, quindi qui si esclude quando è attivo.
  useEffect(() => {
    if (tour !== null) return;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const [suppliers, setSuppliers] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipes, setRecipes] = useState([]);
  // Materie prime già attive nel catalogo vero del cliente (chi ha già
  // l'app configurata): permette di scegliere direttamente da qui in fase
  // di ricetta, senza dover reinserire da zero fornitori/ingredienti già
  // presenti solo per avere qualcosa da selezionare.
  const [existingIngredients, setExistingIngredients] = useState([]);

  useEffect(() => {
    api.get(`/public/intake/${token}`)
      .then(({ data }) => {
        setTenantName(data.tenant_name || "");
        const sub = data.submission || {};
        setSuppliers(sub.suppliers || []);
        setIngredients(sub.ingredients || []);
        setRecipes(sub.recipes || []);
        setSubmittedAt(sub.submitted_at || null);
        setExistingIngredients(data.existing_ingredients || []);
        const hasData = (sub.suppliers?.length || 0) + (sub.ingredients?.length || 0) + (sub.recipes?.length || 0) > 0;
        if (hasData) { setStep(3); setEntryChoice("returning"); } // bentornato: mostra subito il riepilogo di quanto già inserito, niente domanda iniziale
        setState("ok");
      })
      .catch(() => setState(null));
  }, [token]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.put(`/public/intake/${token}`, {
        suppliers: next.suppliers ?? suppliers,
        ingredients: next.ingredients ?? ingredients,
        recipes: next.recipes ?? recipes,
      });
    } catch {
      toast.error("Non sono riuscito a salvare, riprova");
    } finally {
      setSaving(false);
    }
  };

  const addSupplier = (draft) => {
    const next = [...suppliers, { temp_id: tempId(), name: draft.name.trim(), vat: draft.vat.trim(), email: draft.email.trim(), phone: draft.phone.trim() }];
    setSuppliers(next);
    persist({ suppliers: next });
  };
  const updateSupplier = (id, draft) => {
    const next = suppliers.map((s) => (s.temp_id === id ? { ...s, name: draft.name.trim(), vat: draft.vat.trim(), email: draft.email.trim(), phone: draft.phone.trim() } : s));
    setSuppliers(next);
    persist({ suppliers: next });
  };
  const removeSupplier = (id) => {
    const next = suppliers.filter((s) => s.temp_id !== id);
    const nextIngredients = ingredients.map((i) => ({ ...i, supplier_temp_ids: i.supplier_temp_ids.filter((t) => t !== id) }));
    setSuppliers(next);
    setIngredients(nextIngredients);
    persist({ suppliers: next, ingredients: nextIngredients });
  };

  const addIngredient = (draft) => {
    const id = tempId();
    const next = [...ingredients, { temp_id: id, name: draft.name.trim(), allergens: draft.allergens, traces: draft.traces, supplier_temp_ids: draft.supplier_temp_ids }];
    setIngredients(next);
    persist({ ingredients: next });
    return id; // usato dallo step Ricette per selezionare subito l'ingrediente appena creato al volo
  };
  const updateIngredient = (id, draft) => {
    const next = ingredients.map((i) => (i.temp_id === id ? { ...i, name: draft.name.trim(), allergens: draft.allergens, traces: draft.traces, supplier_temp_ids: draft.supplier_temp_ids } : i));
    setIngredients(next);
    persist({ ingredients: next });
  };
  const removeIngredient = (id) => {
    const next = ingredients.filter((i) => i.temp_id !== id);
    const nextRecipes = recipes.map((r) => ({ ...r, ingredient_temp_ids: r.ingredient_temp_ids.filter((t) => t !== id) }));
    setIngredients(next);
    setRecipes(nextRecipes);
    persist({ ingredients: next, recipes: nextRecipes });
  };

  const addRecipe = (draft) => {
    const next = [...recipes, { temp_id: tempId(), name: draft.name.trim(), is_cooked: draft.is_cooked, ingredient_temp_ids: draft.ingredient_temp_ids, existing_ingredient_ids: draft.existing_ingredient_ids, created_at: new Date().toISOString() }];
    setRecipes(next);
    persist({ recipes: next });
  };
  const updateRecipe = (id, draft) => {
    const next = recipes.map((r) => (r.temp_id === id ? { ...r, name: draft.name.trim(), is_cooked: draft.is_cooked, ingredient_temp_ids: draft.ingredient_temp_ids, existing_ingredient_ids: draft.existing_ingredient_ids } : r));
    setRecipes(next);
    persist({ recipes: next });
  };
  const removeRecipe = (id) => {
    const next = recipes.filter((r) => r.temp_id !== id);
    setRecipes(next);
    persist({ recipes: next });
  };

  const finish = async () => {
    await persist({});
    try { await api.post(`/public/intake/${token}/submit`); } catch { /* non bloccante */ }
    setSubmittedAt(new Date().toISOString());
    setSent(true);
  };

  if (state === undefined) {
    return <div className="min-h-screen grid place-items-center bg-haccp-bg"><Spinner /></div>;
  }
  if (state === null) {
    return (
      <div className="min-h-screen bg-haccp-bg flex flex-col items-center p-6">
        <div className="w-full max-w-md">
          <div className="flex justify-center my-8"><Logo size="md" /></div>
          <Card className="p-8 text-center" data-testid="intake-invalid">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold">Link non valido</h1>
            <p className="text-slate-500 font-body mt-2">Chiedi al tuo consulente di inviarti un nuovo link.</p>
          </Card>
        </div>
      </div>
    );
  }
  if (sent) {
    return (
      <div className="min-h-screen bg-haccp-bg flex flex-col items-center p-6">
        <div className="w-full max-w-md space-y-3">
          <div className="flex justify-center my-8"><Logo size="md" /></div>
          <Card className="p-8 text-center" data-testid="intake-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-bold">Dati ricevuti</h1>
            <p className="text-slate-500 font-body mt-2">
              {closed ? "Puoi chiudere questa pagina in sicurezza." : "Grazie! Il tuo consulente rivedrà i dati inviati e li configurerà nella tua app."}
            </p>
          </Card>
          {!closed && (
            <>
              <Button variant="success" className="w-full" onClick={() => setSent(false)} data-testid="intake-add-more">
                <Plus className="w-4 h-4" /> Aggiungi ancora fornitori, ingredienti o ricette
              </Button>
              <Button variant="danger" className="w-full" onClick={() => { setClosed(true); window.close(); }} data-testid="intake-close">
                <X className="w-4 h-4" /> Ho finito, chiudi ed esci
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-haccp-bg p-4 sm:p-6">
      <div className="mx-auto max-w-4xl space-y-5">
        <div className="flex justify-center pt-4"><Logo size="md" /></div>
        <div className="bg-navy text-white rounded-2xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-gold-bright">Pre-configurazione ricette</div>
          <h1 className="font-heading text-2xl font-bold mt-1">{tenantName || "La tua azienda"}</h1>
          <p className="mt-1 text-sm text-white/70">Inserisci fornitori, ingredienti e ricette: il tuo consulente li ritroverà già pronti quando ti consegnerà l'app. Puoi tornare su questo link in qualsiasi momento, i dati restano salvati.</p>
        </div>

        {submittedAt && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="intake-already-sent-banner">
            <strong>Hai già inviato questi dati al tuo consulente il {formatItalianDateTime(submittedAt)}.</strong> Quello che vedi qui sotto è quanto gli hai già mandato — puoi comunque continuare ad aggiungere o correggere qualcosa e inviarlo di nuovo quando vuoi.
          </div>
        )}

        {entryChoice === null ? (
          <Card className="p-6 space-y-4" data-testid="intake-entry-question">
            <div>
              <h2 className="font-heading text-lg font-bold text-navy">Prima di iniziare...</h2>
              <p className="mt-1 text-sm text-slate-500">Hai già fornitori e materie prime caricati nella tua app HACCP?</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => { setEntryChoice("has_catalog"); setStep(2); }} data-testid="intake-entry-has-catalog"
                className="rounded-xl border-2 border-emerald-500 bg-emerald-50 p-4 text-left hover:bg-emerald-100">
                <div className="font-heading font-semibold text-navy">Sì, ho già tutto pronto</div>
                <div className="mt-1 text-xs text-slate-500">Ti porto dritto alle Ricette: scegli le materie prime già in azienda, senza reinserirle.</div>
              </button>
              <button type="button" onClick={() => { setEntryChoice("from_scratch"); setStep(0); }} data-testid="intake-entry-from-scratch"
                className="rounded-xl border-2 border-hairline bg-white p-4 text-left hover:bg-slate-50">
                <div className="font-heading font-semibold text-navy">No, sto iniziando da zero</div>
                <div className="mt-1 text-xs text-slate-500">Iniziamo da Fornitori, poi Ingredienti e infine Ricette.</div>
              </button>
            </div>
            <p className="text-xs text-slate-400">Non sei sicuro? Scegli "Sto iniziando da zero": ti mostriamo comunque tutti i passaggi, e puoi saltare quelli che non ti servono.</p>
          </Card>
        ) : (
          <>
            <Button variant="accent" className="w-full" onClick={() => setTour(0)} data-testid="intake-start-tour">
              <PlayCircle className="w-4 h-4" /> Guarda come funziona (tour guidato in 8 passaggi)
            </Button>

            {tour !== null && (
              <GuidedTour
                index={tour}
                currentWizardStep={step}
                onNext={() => setTour((i) => (i + 1 < TOUR_STEPS.length ? i + 1 : null))}
                onPrev={() => setTour((i) => Math.max(0, i - 1))}
                onClose={() => setTour(null)}
              />
            )}

            <div className="grid gap-4 sm:grid-cols-[200px_minmax(0,1fr)]">
          <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-col sm:overflow-visible sm:pb-0" data-testid="intake-stepper">
            {STEPS.map((s, i) => (
              <button key={s.key} type="button" onClick={() => setStep(i)} data-testid={`intake-step-${s.key}`}
                aria-current={i === step ? "step" : undefined}
                className={`flex min-h-[44px] shrink-0 items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium sm:shrink ${i === step ? "bg-navy text-white" : "bg-white border border-hairline text-slate-600 hover:bg-slate-50"}`}>
                <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs font-bold ${i === step ? "bg-gold text-white" : "bg-slate-100 text-slate-500"}`}>{i + 1}</span>
                <span className="hidden sm:inline">{s.label}</span>
                <s.Icon className="w-4 h-4 shrink-0 sm:hidden" />
              </button>
            ))}
            {saving && <div className="hidden items-center gap-1.5 px-3 py-1 text-xs text-slate-400 sm:flex"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Salvataggio...</div>}
          </div>

          <div className="min-w-0 space-y-4">
            <PageGuide title={`Guida rapida: ${STEPS[step].label}`} {...STEP_GUIDES[STEPS[step].key]} testid="intake-guide" />

            {step === 0 && <SuppliersStep suppliers={suppliers} onAdd={addSupplier} onUpdate={updateSupplier} onRemove={removeSupplier} />}
            {step === 1 && <IngredientsStep ingredients={ingredients} suppliers={suppliers} onAdd={addIngredient} onUpdate={updateIngredient} onRemove={removeIngredient} />}
            {step === 2 && <RecipesStep recipes={recipes} ingredients={ingredients} existingIngredients={existingIngredients} onAdd={addRecipe} onUpdate={updateRecipe} onRemove={removeRecipe} onAddIngredient={addIngredient} />}
            {step === 3 && <SummaryStep suppliers={suppliers} ingredients={ingredients} existingIngredients={existingIngredients} recipes={recipes} goToStep={setStep} submittedAt={submittedAt} />}

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}><ChevronLeft className="w-4 h-4" /> Indietro</Button>
              {step < STEPS.length - 1 ? (
                <Button variant="primary" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>Avanti <ChevronRight className="w-4 h-4" /></Button>
              ) : submittedAt ? (
                <Button variant="success" onClick={() => setStep(0)} data-testid="intake-add-new-round">
                  <Plus className="w-4 h-4" /> Aggiungi nuovi fornitori, ingredienti o ricette
                </Button>
              ) : (
                <Button variant="success" onClick={finish} data-testid="intake-finish">Ho finito, invia al mio consulente</Button>
              )}
            </div>
            {step === STEPS.length - 1 && submittedAt && (
              <div className="text-center">
                <button type="button" onClick={finish} className="text-xs text-slate-400 underline" data-testid="intake-finish-again">Non ho altro da aggiungere, ho già finito</button>
              </div>
            )}
          </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SuppliersStep({ suppliers, onAdd, onUpdate, onRemove }) {
  const empty = { name: "", vat: "", email: "", phone: "" };
  const [draft, setDraft] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [attempted, setAttempted] = useState(false);

  const startEdit = (s) => { setEditingId(s.temp_id); setDraft({ name: s.name, vat: s.vat, email: s.email, phone: s.phone }); setAttempted(false); };
  const cancelEdit = () => { setEditingId(null); setDraft(empty); setAttempted(false); };
  const handleRemove = (id) => { if (id === editingId) cancelEdit(); onRemove(id); };
  const submit = () => {
    if (!draft.name.trim()) { setAttempted(true); toast.error("Inserisci il nome del fornitore"); return; }
    if (editingId) onUpdate(editingId, draft); else onAdd(draft);
    setDraft(empty); setEditingId(null); setAttempted(false);
  };
  const nameError = attempted && !draft.name.trim();

  return (
    <Card className="p-5 space-y-4" data-testid="intake-suppliers">
      <p className="text-sm text-slate-500">Chi ti fornisce le materie prime? Aggiungi un fornitore alla volta.</p>
      <Table columns={["Ragione sociale", "Contatti", "Azioni"]} empty="Nessun fornitore inserito.">
        {suppliers.map((s) => (
          <tr key={s.temp_id} className={editingId === s.temp_id ? "bg-amber-50/50" : ""}>
            <td className={`${tdCls} font-medium text-navy`}>{s.name}</td>
            <td className={`${tdCls} text-slate-500`}>{[s.vat, s.email, s.phone].filter(Boolean).join(" · ") || "—"}</td>
            <td className={tdCls}><RowActions label={s.name} onEdit={() => startEdit(s)} onRemove={() => handleRemove(s.temp_id)} /></td>
          </tr>
        ))}
      </Table>
      <div className="grid gap-3 sm:grid-cols-2 rounded-xl border border-hairline bg-slate-50 p-4">
        {editingId && (
          <div className="sm:col-span-2 flex items-center justify-between rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-800">
            Stai modificando un fornitore già inserito
            <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 text-amber-800 underline"><X className="w-3.5 h-3.5" /> Annulla</button>
          </div>
        )}
        <Field label="Ragione sociale *">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Es. Alimentari Rossi Srl" className={nameError ? errCls : ""} data-testid="intake-supplier-name" />
        </Field>
        <Field label="Partita IVA (facoltativa)"><Input value={draft.vat} onChange={(e) => setDraft({ ...draft, vat: e.target.value.replace(/\D/g, "").slice(0, 11) })} inputMode="numeric" maxLength={11} /></Field>
        <Field label="Email (facoltativa)"><Input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field>
        <Field label="Telefono (facoltativo)"><Input value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></Field>
        {nameError && <p className="sm:col-span-2 -mt-2 text-xs font-medium text-red-600">Il nome del fornitore è obbligatorio.</p>}
        <Button variant="success" className="sm:col-span-2" onClick={submit} data-testid="intake-add-supplier">
          {editingId ? <><Pencil className="w-4 h-4" /> Salva modifiche</> : <><Plus className="w-4 h-4" /> Aggiungi fornitore</>}
        </Button>
      </div>
    </Card>
  );
}

function IngredientsStep({ ingredients, suppliers, onAdd, onUpdate, onRemove }) {
  const empty = { name: "", allergens: [], traces: [], supplier_temp_ids: [] };
  const [draft, setDraft] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const startEdit = (i) => { setEditingId(i.temp_id); setDraft({ name: i.name, allergens: i.allergens || [], traces: i.traces || [], supplier_temp_ids: i.supplier_temp_ids || [] }); setAttempted(false); };
  const cancelEdit = () => { setEditingId(null); setDraft(empty); setAttempted(false); };
  const handleRemove = (id) => { if (id === editingId) cancelEdit(); onRemove(id); };
  const submit = () => {
    if (!draft.name.trim()) { setAttempted(true); toast.error("Inserisci il nome dell'ingrediente"); return; }
    if (editingId) onUpdate(editingId, draft); else onAdd(draft);
    setDraft(empty); setEditingId(null); setAttempted(false);
  };
  const suggestAllergens = async () => {
    const name = draft.name.trim();
    if (!name || draft.allergens.length > 0) return;
    setSuggesting(true);
    try {
      const { data } = await api.get("/public/intake/infer-allergens", { params: { name } });
      if (data.allergens?.length > 0) {
        setDraft((current) => (current.allergens.length > 0 ? current : { ...current, allergens: data.allergens }));
      }
    } catch {
      // suggerimento facoltativo: se fallisce, il cliente seleziona a mano
    } finally {
      setSuggesting(false);
    }
  };
  const nameError = attempted && !draft.name.trim();

  return (
    <Card className="p-5 space-y-4" data-testid="intake-ingredients">
      <p className="text-sm text-slate-500">Quali materie prime usi nelle tue ricette? Gli allergeni vengono suggeriti da soli dal nome.</p>
      <Table columns={["Nome", "Allergeni", "Fornitore", "Azioni"]} empty="Nessun ingrediente inserito.">
        {ingredients.map((i) => (
          <tr key={i.temp_id} className={editingId === i.temp_id ? "bg-amber-50/50" : ""}>
            <td className={`${tdCls} font-medium text-navy`}>{i.name}</td>
            <td className={tdCls}>
              {i.allergens?.length > 0
                ? <div className="flex flex-wrap gap-1">{i.allergens.map((a) => <span key={a} className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700">{allergenLabel(a)}</span>)}</div>
                : <span className="text-slate-400">—</span>}
            </td>
            <td className={`${tdCls} text-slate-500`}>{i.supplier_temp_ids.map((t) => suppliers.find((s) => s.temp_id === t)?.name).filter(Boolean).join(", ") || "—"}</td>
            <td className={tdCls}><RowActions label={i.name} onEdit={() => startEdit(i)} onRemove={() => handleRemove(i.temp_id)} /></td>
          </tr>
        ))}
      </Table>
      <div className="space-y-3 rounded-xl border border-hairline bg-slate-50 p-4">
        {editingId && (
          <div className="flex items-center justify-between rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-800">
            Stai modificando un ingrediente già inserito
            <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 text-amber-800 underline"><X className="w-3.5 h-3.5" /> Annulla</button>
          </div>
        )}
        <Field label="Nome ingrediente *">
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} onBlur={suggestAllergens} placeholder="Es. Farina 00" className={nameError ? errCls : ""} data-testid="intake-ingredient-name" />
        </Field>
        {nameError && <p className="-mt-2 text-xs font-medium text-red-600">Il nome dell'ingrediente è obbligatorio.</p>}
        <div>
          <div className="mb-2 text-xs uppercase tracking-wider text-slate-400">
            Allergeni presenti <span className="normal-case font-normal text-slate-400">(campo non obbligatorio — da compilare solo se il sistema automatico non riconosce gli allergeni presenti)</span>
          </div>
          {suggesting && <div className="mb-2 flex items-center gap-1.5 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Sto controllando automaticamente gli allergeni...</div>}
          <div className="flex flex-wrap gap-1.5">
            {ALLERGENS.map((a) => (
              <button key={a.key} type="button" onClick={() => setDraft({ ...draft, allergens: toggleIn(draft.allergens, a.key) })}
                className={`text-xs px-2.5 py-1.5 rounded-full border min-h-[36px] ${draft.allergens.includes(a.key) ? "bg-red-600 text-white border-red-600" : "border-hairline text-slate-500 bg-white"}`}>
                {a.label}
              </button>
            ))}
          </div>
        </div>
        <div className={`rounded-lg border p-3 ${draft.supplier_temp_ids.length === 0 ? "border-amber-300 bg-amber-50/60" : "border-emerald-300 bg-emerald-50/40"}`}>
          <div className="flex items-center gap-2 text-sm font-semibold text-navy"><Truck className="w-4 h-4 shrink-0" /> Chi ti fornisce questo ingrediente?</div>
          {suppliers.length === 0 ? (
            <p className="mt-1 text-xs text-amber-800">Non hai ancora aggiunto nessun fornitore (passo 1). Puoi tornare indietro per aggiungerne uno, oppure salvare l'ingrediente e collegarlo più tardi con la matita.</p>
          ) : (
            <>
              <p className="mt-0.5 text-xs text-slate-500">Facoltativo, ma utile al tuo consulente: tocca il fornitore giusto qui sotto.</p>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="intake-ingredient-suppliers">
                {suppliers.map((s) => (
                  <button key={s.temp_id} type="button" onClick={() => setDraft({ ...draft, supplier_temp_ids: toggleIn(draft.supplier_temp_ids, s.temp_id) })}
                    className={`text-xs px-2.5 py-1.5 rounded-full border min-h-[36px] ${draft.supplier_temp_ids.includes(s.temp_id) ? "bg-emerald-600 text-white border-emerald-600" : "border-hairline text-slate-500 bg-white"}`}>
                    {s.name}
                  </button>
                ))}
              </div>
              {draft.supplier_temp_ids.length === 0 && <p className="mt-2 text-xs font-medium text-amber-700">Non hai ancora collegato nessun fornitore a questo ingrediente.</p>}
            </>
          )}
        </div>
        <Button variant="success" className="w-full" onClick={submit} data-testid="intake-add-ingredient">
          {editingId ? <><Pencil className="w-4 h-4" /> Salva modifiche</> : <><Plus className="w-4 h-4" /> Aggiungi ingrediente</>}
        </Button>
      </div>
    </Card>
  );
}

function recipeIngredientNames(recipe, ingredients, existingIngredients) {
  return [
    ...(recipe.ingredient_temp_ids || []).map((t) => ingredients.find((i) => i.temp_id === t)?.name),
    ...(recipe.existing_ingredient_ids || []).map((id) => existingIngredients.find((i) => i.id === id)?.name),
  ].filter(Boolean);
}

function RecipesStep({ recipes, ingredients, existingIngredients, onAdd, onUpdate, onRemove, onAddIngredient }) {
  const empty = { name: "", is_cooked: false, ingredient_temp_ids: [], existing_ingredient_ids: [] };
  const [draft, setDraft] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [existingFilter, setExistingFilter] = useState("");
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddName, setQuickAddName] = useState("");
  const [quickAddBusy, setQuickAddBusy] = useState(false);

  const hasAnyIngredient = ingredients.length > 0 || existingIngredients.length > 0;
  const selectedCount = draft.ingredient_temp_ids.length + draft.existing_ingredient_ids.length;

  const startEdit = (r) => {
    setEditingId(r.temp_id);
    setDraft({ name: r.name, is_cooked: r.is_cooked, ingredient_temp_ids: r.ingredient_temp_ids || [], existing_ingredient_ids: r.existing_ingredient_ids || [] });
    setAttempted(false);
  };
  const cancelEdit = () => { setEditingId(null); setDraft(empty); setAttempted(false); };
  const handleRemove = (id) => { if (id === editingId) cancelEdit(); onRemove(id); };
  const submit = () => {
    if (!draft.name.trim() || selectedCount === 0) {
      setAttempted(true);
      toast.error(!draft.name.trim() ? "Inserisci il nome della ricetta" : "Seleziona almeno un ingrediente");
      return;
    }
    if (editingId) onUpdate(editingId, draft); else onAdd(draft);
    setDraft(empty); setEditingId(null); setAttempted(false);
  };
  const quickAddIngredient = async () => {
    const name = quickAddName.trim();
    if (!name) return;
    const norm = normalizedName(name);
    // Evita di creare un doppione se il nome esiste già: seleziona quello
    // vero invece di aggiungerne una copia al modulo.
    const existingMatch = existingIngredients.find((i) => normalizedName(i.name) === norm);
    if (existingMatch) {
      setDraft((cur) => ({ ...cur, existing_ingredient_ids: toggleIn(cur.existing_ingredient_ids, existingMatch.id) }));
      toast.success(`"${existingMatch.name}" è già in azienda: selezionata`);
      setQuickAddName(""); setQuickAddOpen(false);
      return;
    }
    const sessionMatch = ingredients.find((i) => normalizedName(i.name) === norm);
    if (sessionMatch) {
      setDraft((cur) => ({ ...cur, ingredient_temp_ids: toggleIn(cur.ingredient_temp_ids, sessionMatch.temp_id) }));
      toast.success(`"${sessionMatch.name}" era già stata aggiunta: selezionata`);
      setQuickAddName(""); setQuickAddOpen(false);
      return;
    }
    setQuickAddBusy(true);
    let allergens = [];
    try {
      const { data } = await api.get("/public/intake/infer-allergens", { params: { name } });
      allergens = data.allergens || [];
    } catch {
      // suggerimento facoltativo: se fallisce si può sempre correggere dopo, nello step Ingredienti
    }
    const newId = onAddIngredient({ name, allergens, traces: [], supplier_temp_ids: [] });
    setDraft((cur) => ({ ...cur, ingredient_temp_ids: [...cur.ingredient_temp_ids, newId] }));
    toast.success(`"${name}" aggiunta e selezionata per questa ricetta`);
    setQuickAddName(""); setQuickAddOpen(false); setQuickAddBusy(false);
  };
  const nameError = attempted && !draft.name.trim();
  const ingredientsError = attempted && selectedCount === 0;
  const filteredExisting = existingIngredients.filter((i) => normalizedName(i.name).includes(normalizedName(existingFilter)));
  // Il modulo tiene traccia di tutto ciò che è mai stato scritto qui, anche
  // se già importato nel catalogo vero: senza questo filtro lo stesso nome
  // (es. "Acqua") comparirebbe due volte, una per gruppo, confondendo chi
  // pensa di non averlo mai inserito.
  const existingNames = new Set(existingIngredients.map((i) => normalizedName(i.name)));
  const sessionIngredients = ingredients.filter((i) => !existingNames.has(normalizedName(i.name)));

  return (
    <Card className="p-5 space-y-4" data-testid="intake-recipes">
      <p className="text-sm text-slate-500">Le tue ricette: nome, ingredienti usati e se prevedono una cottura. Il resto lo completerà il tuo consulente.</p>
      <Table columns={["Nome", "Ingredienti", "Cottura", "Azioni"]} empty="Nessuna ricetta inserita.">
        {recipes.map((r) => (
          <tr key={r.temp_id} className={editingId === r.temp_id ? "bg-amber-50/50" : ""}>
            <td className={`${tdCls} font-medium text-navy`}>{r.name}</td>
            <td className={`${tdCls} text-slate-500`}>{recipeIngredientNames(r, ingredients, existingIngredients).join(", ") || "—"}</td>
            <td className={tdCls}>{r.is_cooked ? <span className="inline-flex items-center gap-1 text-amber-600"><Flame className="w-3.5 h-3.5" /> Sì</span> : "No"}</td>
            <td className={tdCls}><RowActions label={r.name} onEdit={() => startEdit(r)} onRemove={() => handleRemove(r.temp_id)} /></td>
          </tr>
        ))}
      </Table>
      {!hasAnyIngredient && !quickAddOpen ? (
        <div className="rounded-lg border border-dashed border-hairline bg-slate-50 p-4 text-center text-sm text-slate-500 space-y-2">
          <p>Non hai ancora nessuna materia prima da scegliere.</p>
          <Button type="button" variant="outline" onClick={() => setQuickAddOpen(true)} data-testid="intake-recipe-quick-add-open">
            <Plus className="w-4 h-4" /> Aggiungi materia prima
          </Button>
        </div>
      ) : (
        <div className="space-y-3 rounded-xl border border-hairline bg-slate-50 p-4">
          {editingId && (
            <div className="flex items-center justify-between rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-800">
              Stai modificando una ricetta già inserita
              <button type="button" onClick={cancelEdit} className="inline-flex items-center gap-1 text-amber-800 underline"><X className="w-3.5 h-3.5" /> Annulla</button>
            </div>
          )}
          <Field label="Nome ricetta *">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Es. Lasagna alla bolognese" className={nameError ? errCls : ""} data-testid="intake-recipe-name" />
          </Field>
          {nameError && <p className="-mt-2 text-xs font-medium text-red-600">Il nome della ricetta è obbligatorio.</p>}

          <div className={`rounded-lg p-2 ${ingredientsError ? "ring-1 ring-red-300 bg-red-50/40" : ""}`}>
            {existingIngredients.length > 0 && (
              <div className="mb-3">
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Già presenti in azienda</div>
                {existingIngredients.length > 8 && (
                  <Input value={existingFilter} onChange={(e) => setExistingFilter(e.target.value)} placeholder="Cerca tra le materie prime già in azienda..." className="mb-2" />
                )}
                <div className="flex flex-wrap gap-1.5" data-testid="intake-recipe-existing-ingredients">
                  {filteredExisting.length === 0 ? (
                    <p className="text-xs text-slate-400">Nessuna corrispondenza.</p>
                  ) : filteredExisting.map((i) => (
                    <button key={i.id} type="button" onClick={() => setDraft({ ...draft, existing_ingredient_ids: toggleIn(draft.existing_ingredient_ids, i.id) })}
                      className={`text-xs px-2.5 py-1.5 rounded-full border min-h-[36px] ${draft.existing_ingredient_ids.includes(i.id) ? "bg-emerald-600 text-white border-emerald-600" : "border-hairline text-slate-500 bg-white"}`}>
                      {i.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {sessionIngredients.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Aggiunte qui, non ancora nel catalogo</div>
                <div className="flex flex-wrap gap-1.5">
                  {sessionIngredients.map((i) => (
                    <button key={i.temp_id} type="button" onClick={() => setDraft({ ...draft, ingredient_temp_ids: toggleIn(draft.ingredient_temp_ids, i.temp_id) })}
                      className={`text-xs px-2.5 py-1.5 rounded-full border min-h-[36px] ${draft.ingredient_temp_ids.includes(i.temp_id) ? "bg-navy text-white border-navy" : "border-hairline text-slate-500 bg-white"}`}>
                      {i.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {ingredientsError && <p className="mt-2 text-xs font-medium text-red-600">Seleziona almeno un ingrediente.</p>}

            {quickAddOpen ? (
              <div className="mt-3 flex flex-col gap-2 rounded-lg border border-dashed border-hairline bg-white p-3 sm:flex-row sm:items-center">
                <Input value={quickAddName} onChange={(e) => setQuickAddName(e.target.value)} placeholder="Nome della materia prima mancante" className="flex-1" data-testid="intake-recipe-quick-add-name" />
                <div className="flex gap-2">
                  <Button type="button" variant="success" loading={quickAddBusy} onClick={quickAddIngredient} data-testid="intake-recipe-quick-add-confirm"><Plus className="w-4 h-4" /> Aggiungi e seleziona</Button>
                  <Button type="button" variant="outline" onClick={() => { setQuickAddOpen(false); setQuickAddName(""); }}><X className="w-4 h-4" /></Button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setQuickAddOpen(true)} className="mt-3 text-xs font-semibold text-amber-700 underline" data-testid="intake-recipe-quick-add-open">
                + Ti manca una materia prima per questa ricetta? Aggiungila qui
              </button>
            )}
          </div>

          <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-hairline bg-white p-3 text-sm w-fit">
            <input type="checkbox" checked={draft.is_cooked} onChange={(e) => setDraft({ ...draft, is_cooked: e.target.checked })} className="h-5 w-5 rounded accent-amber-500" />
            <span className="font-medium text-navy">Sottoposta a cottura</span>
          </label>
          <Button variant="success" className="w-full" onClick={submit} data-testid="intake-add-recipe">
            {editingId ? <><Pencil className="w-4 h-4" /> Salva modifiche</> : <><Plus className="w-4 h-4" /> Aggiungi ricetta</>}
          </Button>
        </div>
      )}
    </Card>
  );
}

function SummaryStep({ suppliers, ingredients, existingIngredients, recipes, goToStep, submittedAt }) {
  return (
    <Card className="p-5 space-y-5" data-testid="intake-summary">
      {submittedAt ? (
        <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
          <strong>Questo è ciò che hai già inviato</strong> al tuo consulente il {formatItalianDateTime(submittedAt)}. Se vuoi aggiungere altro, premi qui sotto "Aggiungi nuovi fornitori, ingredienti o ricette"; se invece va bene così, tocca "Non ho altro da aggiungere".
        </p>
      ) : (
        <p className="text-sm text-slate-500">Controlla tutto prima di inviare. Se manca qualcosa, torna allo step giusto.</p>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-navy">Fornitori ({suppliers.length})</h3>
          <button type="button" onClick={() => goToStep(0)} className="text-xs font-semibold text-amber-700 underline">Modifica</button>
        </div>
        <Table columns={["Ragione sociale", "Contatti"]} empty="Nessun fornitore inserito.">
          {suppliers.map((s) => (
            <tr key={s.temp_id}><td className={`${tdCls} font-medium text-navy`}>{s.name}</td><td className={`${tdCls} text-slate-500`}>{[s.vat, s.email, s.phone].filter(Boolean).join(" · ") || "—"}</td></tr>
          ))}
        </Table>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-navy">Ingredienti ({ingredients.length})</h3>
          <button type="button" onClick={() => goToStep(1)} className="text-xs font-semibold text-amber-700 underline">Modifica</button>
        </div>
        <Table columns={["Nome", "Allergeni"]} empty="Nessun ingrediente inserito.">
          {ingredients.map((i) => (
            <tr key={i.temp_id}>
              <td className={`${tdCls} font-medium text-navy`}>{i.name}</td>
              <td className={tdCls}>{i.allergens?.length > 0 ? i.allergens.map(allergenLabel).join(", ") : <span className="text-slate-400">—</span>}</td>
            </tr>
          ))}
        </Table>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-navy">Ricette ({recipes.length})</h3>
          <button type="button" onClick={() => goToStep(2)} className="text-xs font-semibold text-amber-700 underline">Modifica</button>
        </div>
        <Table columns={["Nome", "Ingredienti", "Cottura"]} empty="Nessuna ricetta inserita.">
          {recipes.map((r) => (
            <tr key={r.temp_id}>
              <td className={`${tdCls} font-medium text-navy`}>{r.name}</td>
              <td className={`${tdCls} text-slate-500`}>{recipeIngredientNames(r, ingredients, existingIngredients).join(", ") || "—"}</td>
              <td className={tdCls}>{r.is_cooked ? "Sì" : "No"}</td>
            </tr>
          ))}
        </Table>
      </div>

      {(suppliers.length === 0 || ingredients.length === 0 || recipes.length === 0) && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Puoi inviare anche così, ma se hai dimenticato qualcosa puoi tornare indietro e completarlo prima di premere "Invia".</p>
      )}
    </Card>
  );
}
